import { useEffect, useState } from "react";

import type { Editor } from "@tiptap/react";

import { MentionSuggestion } from "./mention-suggestion";

export function useMentionSuggestion(editor: Editor | null) {
  const [mentionState, setMentionState] = useState<{
    query: string;
    position: { top: number; left: number } | null;
  }>({
    query: "",
    position: null,
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const checkMention = () => {
      if (!editor) return;

      const { state } = editor;
      const { doc, selection } = state;
      const { $from } = selection;
      const pos = $from.pos;

      // Get text before cursor
      const textBefore = doc.textBetween(Math.max(0, pos - 50), pos, " ", " ");
      
      // Check for @ mention pattern - match @ followed by any characters (including spaces, but stop at whitespace boundaries)
      // This allows filtering as the user types
      const match = textBefore.match(/@([^\s@]*)$/);

      if (match) {
        const query = match[1];
        try {
          const coords = editor.view.coordsAtPos(pos);

          setMentionState({
            query,
            position: {
              top: coords.bottom + window.scrollY,
              left: coords.left + window.scrollX,
            },
          });
        } catch (error) {
          setMentionState({
            query: "",
            position: null,
          });
        }
      } else {
        setMentionState({
          query: "",
          position: null,
        });
      }
    };

    editor.on("update", checkMention);
    editor.on("selectionUpdate", checkMention);

    return () => {
      editor.off("update", checkMention);
      editor.off("selectionUpdate", checkMention);
    };
  }, [editor]);

  const handleSelect = (userId: string, label: string) => {
    if (!editor) return;

    const { state } = editor;
    const { doc, selection } = state;
    const { $from } = selection;
    const pos = $from.pos;

    // Get text before cursor to find @ position
    const textBefore = doc.textBetween(Math.max(0, pos - 50), pos);
    const match = textBefore.match(/@([^\s@]*)$/);

    if (match) {
      const startPos = pos - match[0].length; // Position of @ (includes @ and query)
      const endPos = pos; // Current position

      // Use editor commands to delete @ and typed text, then insert mention
      editor
        .chain()
        .focus()
        .deleteRange({ from: startPos, to: endPos })
        .insertContent({
          type: "mention",
          attrs: {
            id: userId,
            label: label,
          },
        })
        .insertContent(" ")
        .run();
    }

    setMentionState({
      query: "",
      position: null,
    });
  };

  const handleClose = () => {
    setMentionState({
      query: "",
      position: null,
    });
  };

  return (
    <MentionSuggestion
      editor={editor}
      query={mentionState.query}
      position={mentionState.position}
      onSelect={handleSelect}
      onClose={handleClose}
    />
  );
}

